import { useState } from 'react';
import { orders } from '@/mocks/orders';
import type { Order } from '@/mocks/orders';

interface MessageTemplate {
  id: string;
  category: string;
  title: string;
  template: string;
  icon: string;
  color: string;
}

const messageTemplates: MessageTemplate[] = [
  {
    id: 'new-order',
    category: 'Sipariş',
    title: 'Yeni Sipariş Alındı',
    template: 'Merhaba {{musteri_adi}}, Tablocum.11\'e hoş geldiniz! {{urun_adi}} siparişiniz başarıyla alınmıştır. Sipariş numaranız: {{siparis_no}}. Üretim süreci başladığında sizi bilgilendireceğiz. Toplam tutar: {{tutar}} ₺.',
    icon: 'ri-shopping-bag-line',
    color: 'bg-status-blue',
  },
  {
    id: 'drawing-started',
    category: 'Üretim',
    title: 'Çizim Aşaması Başladı',
    template: 'Merhaba {{musteri_adi}}, siparişiniz için çizim aşamasına geçilmiştir. Tasarım hazırlandığında onayınız için size iletilecektir.',
    icon: 'ri-pencil-ruler-line',
    color: 'bg-status-purple',
  },
  {
    id: 'drawing-approval',
    category: 'Üretim',
    title: 'Çizim Onayı',
    template: 'Merhaba {{musteri_adi}}, {{urun_adi}} tasarımınız hazır! Görseli inceleyip onay vermenizi rica ederiz. Onay sonrası kesim aşamasına geçilecektir.',
    icon: 'ri-check-double-line',
    color: 'bg-status-purple',
  },
  {
    id: 'cutting-started',
    category: 'Üretim',
    title: 'Kesim Aşaması Başladı',
    template: 'Merhaba {{musteri_adi}}, onaylanan ürününüz kesim aşamasına alınmıştır. Üretim süreci devam etmektedir.',
    icon: 'ri-scissors-cut-line',
    color: 'bg-status-orange',
  },
  {
    id: 'lighting-test',
    category: 'Üretim',
    title: 'Aydınlatma ve Test',
    template: 'Merhaba {{musteri_adi}}, ürününüz aydınlatma ve test aşamasındadır. Son kontroller tamamlandıktan sonra paketleme sürecine geçilecektir.',
    icon: 'ri-lightbulb-line',
    color: 'bg-status-yellow',
  },
  {
    id: 'packing',
    category: 'Kargo',
    title: 'Kargoya Hazırlanıyor',
    template: 'Merhaba {{musteri_adi}}, ürününüz tamamlandı ve kargoya hazırlanmaktadır. Kargo takip bilgisi oluştuğunda size iletilecektir.',
    icon: 'ri-box-3-line',
    color: 'bg-status-navy',
  },
  {
    id: 'cargo-sent',
    category: 'Kargo',
    title: 'Kargoya Verildi',
    template: 'Merhaba {{musteri_adi}}, Tablocum.11 siparişiniz kargoya verilmiştir.\nKargo firması: {{kargo_firmasi}}\nTakip numarası: {{takip_no}}\nÜrün teslim sürecinizi bu takip numarası ile kontrol edebilirsiniz.\nTahmini teslim: {{tahmini_teslim}}',
    icon: 'ri-truck-line',
    color: 'bg-status-sky',
  },
  {
    id: 'delivery-thanks',
    category: 'Teslimat',
    title: 'Teslimat Sonrası Teşekkür',
    template: 'Merhaba {{musteri_adi}}, Tablocum.11 siparişinizin size ulaşmış olmasını umuyoruz. Güzel günlerde kullanmanız dileğiyle. Memnuniyetinizi bizimle paylaşmanız bizi mutlu eder.',
    icon: 'ri-heart-line',
    color: 'bg-status-green',
  },
  {
    id: 'payment-info',
    category: 'Ödeme',
    title: 'Kapıda Ödeme Bilgilendirme',
    template: 'Merhaba {{musteri_adi}}, siparişiniz kapıda ödeme olarak hazırlanmıştır. Kargo teslimatı sırasında {{tutar}} ₺ tutarındaki ödemeyi yapmanız gerekmektedir. Kargonuz yola çıktığında takip numaranızı paylaşacağız.',
    icon: 'ri-money-dollar-circle-line',
    color: 'bg-status-orange',
  },
  {
    id: 'delay',
    category: 'Bilgilendirme',
    title: 'Gecikme Bilgilendirme',
    template: 'Merhaba {{musteri_adi}}, {{urun_adi}} siparişinizde beklenmedik bir gecikme yaşanmıştır. Ürününüz şu anda {{uretim_asamasi}} aşamasındadır. En kısa sürede tamamlanıp tarafınıza bilgi verilecektir. Anlayışınız için teşekkür ederiz.',
    icon: 'ri-alert-line',
    color: 'bg-status-red',
  },
  {
    id: 'daily-update',
    category: 'Bilgilendirme',
    title: 'Günlük Durum Bilgilendirme',
    template: 'Merhaba {{musteri_adi}}, Tablocum.11 siparişinizin güncel durumu: {{siparis_durumu}}. Ürününüz şu anda {{uretim_asamasi}} aşamasındadır. Her aşama tamamlandığında sizi bilgilendireceğiz.',
    icon: 'ri-chat-3-line',
    color: 'bg-gold-500',
  },
];

const activeOrders = orders.filter(o => o.status !== 'teslim' && o.status !== 'iptal');

export default function MessagesPage() {
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [previewMessage, setPreviewMessage] = useState('');
  const [activeCategory, setActiveCategory] = useState('Tümü');

  const categories = ['Tümü', ...Array.from(new Set(messageTemplates.map(t => t.category)))];

  const filteredTemplates = activeCategory === 'Tümü'
    ? messageTemplates
    : messageTemplates.filter(t => t.category === activeCategory);

  const fillTemplate = (template: string, order: Order): string => {
    return template
      .replace(/\{\{musteri_adi\}\}/g, order.customerName)
      .replace(/\{\{siparis_no\}\}/g, order.id)
      .replace(/\{\{urun_adi\}\}/g, order.productName)
      .replace(/\{\{siparis_durumu\}\}/g, order.productionStage)
      .replace(/\{\{uretim_asamasi\}\}/g, order.productionStage)
      .replace(/\{\{kargo_firmasi\}\}/g, order.cargoCompany || 'Belirtilecek')
      .replace(/\{\{takip_no\}\}/g, order.cargoTrackingNo || 'Eklenecek')
      .replace(/\{\{tutar\}\}/g, order.amount.toLocaleString('tr-TR'))
      .replace(/\{\{tahmini_teslim\}\}/g, order.estimatedDelivery);
  };

  const handleSelectTemplate = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    if (selectedOrder) {
      setPreviewMessage(fillTemplate(template.template, selectedOrder));
    } else {
      setPreviewMessage(template.template);
    }
  };

  const handleSelectOrder = (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    setSelectedOrder(order || null);
    if (selectedTemplate && order) {
      setPreviewMessage(fillTemplate(selectedTemplate.template, order));
    } else if (selectedTemplate) {
      setPreviewMessage(selectedTemplate.template);
    }
  };

  const openWhatsApp = () => {
    if (!selectedOrder || !previewMessage) return;
    window.open(
      `https://wa.me/${selectedOrder.phone}?text=${encodeURIComponent(previewMessage)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Müşteri Mesajları</h2>
          <p className="text-sm text-stone-500 mt-0.5">Şablon seç, müşteri seç, WhatsApp ile gönder</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Templates */}
        <div className="lg:col-span-2 space-y-4">
          {/* Category tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-gold-500 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Template Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() => handleSelectTemplate(template)}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                  selectedTemplate?.id === template.id
                    ? 'border-gold-400 ring-1 ring-gold-400/30 shadow-sm'
                    : 'border-stone-200/80 hover:border-stone-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${template.color} bg-opacity-10`}>
                    <i className={`${template.icon} text-sm ${template.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-stone-800">{template.title}</h4>
                    <p className="text-xs text-stone-500 mt-0.5">{template.category}</p>
                    <p className="text-xs text-stone-400 mt-2 line-clamp-2 italic">
                      "{template.template.slice(0, 100)}..."
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Preview & Send */}
        <div className="space-y-4">
          {/* Order Selection */}
          <div className="bg-white rounded-xl border border-stone-200/80 p-4">
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Müşteri Seçimi</h4>
            <select
              value={selectedOrder?.id || ''}
              onChange={(e) => handleSelectOrder(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400"
            >
              <option value="">Müşteri seçin (zorunlu değil)</option>
              {activeOrders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.id} - {order.customerName} ({order.productName})
                </option>
              ))}
            </select>
            {selectedOrder && (
              <div className="mt-3 pt-3 border-t border-stone-100">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-stone-500">{selectedOrder.phone.replace('90', '0')}</span>
                  <span className="text-stone-500">{selectedOrder.city}</span>
                </div>
              </div>
            )}
          </div>

          {/* Message Preview */}
          <div className="bg-white rounded-xl border border-stone-200/80 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Mesaj Önizleme</h4>
              {selectedTemplate && (
                <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{selectedTemplate.category}</span>
              )}
            </div>
            {previewMessage ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{previewMessage}</p>
              </div>
            ) : (
              <div className="bg-stone-50 rounded-xl p-6 text-center">
                <i className="ri-chat-3-line text-2xl text-stone-300 block mb-2" />
                <p className="text-sm text-stone-400">Şablon seçerek mesaj önizlemesini görün</p>
              </div>
            )}
          </div>

          {/* Send Button */}
          {previewMessage && selectedOrder && (
            <button
              onClick={openWhatsApp}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white text-sm font-semibold rounded-xl hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg" />
              WhatsApp'ta Aç ve Gönder
            </button>
          )}

          {previewMessage && !selectedOrder && (
            <div className="bg-status-yellow/10 border border-status-yellow/30 rounded-xl p-3 text-center">
              <p className="text-xs text-status-yellow font-medium">
                <i className="ri-information-line mr-1" />
                Göndermek için bir müşteri seçmelisiniz
              </p>
            </div>
          )}

          {/* Dynamic Fields Info */}
          <div className="bg-stone-50 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Dinamik Alanlar</h4>
            <div className="flex flex-wrap gap-1.5">
              {['musteri_adi', 'siparis_no', 'urun_adi', 'siparis_durumu', 'uretim_asamasi', 'kargo_firmasi', 'takip_no', 'tutar', 'tahmini_teslim'].map((field) => (
                <span key={field} className="text-[10px] bg-white border border-stone-200 px-1.5 py-0.5 rounded text-stone-500 font-mono">
                  {`{{${field}}}`}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}